import { getActiveRepairOrderViewport } from '@/ShopManagement/repairOrderViewport';

const INVALID_CONTROL_SELECTOR = ['[aria-invalid="true"]', '.p-invalid'].join(', ');

const FIELD_ERROR_SELECTOR = [
  '.p-message-error',
  '[data-pc-severity="error"]',
  '[data-p-severity="error"]',
].join(', ');

const ERROR_BANNER_SELECTOR = '.p-message.p-message-error';

const ERROR_BANNER_OBSERVER_TIMEOUT = 10000;

const pendingSubmitNavigations = new WeakMap();
const submitEventNavigations = new WeakMap();
const managedDialogNavigations = new WeakMap();
const queuedNavigations = new WeakMap();

const FOCUSABLE_CONTROL_SELECTOR = [
  'input:not([type="hidden"])',
  'textarea',
  'select',
  '[role="combobox"]',
  '[role="spinbutton"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[contenteditable="true"]',
  'button[aria-haspopup]',
].join(', ');

const isElement = (value) => typeof Element !== 'undefined' && value instanceof Element;

const sourceElement = (scope) => {
  if (isElement(scope)) return scope;
  if (!scope || typeof scope !== 'object') return null;

  const candidates = [
    scope.originalEvent?.target,
    scope.currentTarget,
    scope.target,
    scope.$el,
    scope.el,
    scope.form,
    scope.form?.$el,
    scope.root,
    scope.root?.$el,
    scope.value,
    scope.value?.$el,
    scope.value?.el,
  ];

  return candidates.find(isElement) || null;
};

const isVisible = (element) => {
  if (!isElement(element) || !element.isConnected) return false;
  if (element.closest('[hidden], [aria-hidden="true"], [inert]')) return false;

  const style = window.getComputedStyle(element);
  return (
    style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0
  );
};

const dialogZIndex = (dialog) => {
  const zIndex = Number.parseInt(window.getComputedStyle(dialog.closest('.p-dialog-mask') || dialog).zIndex, 10);
  return Number.isNaN(zIndex) ? 0 : zIndex;
};

const topmostActiveDialog = () => {
  if (typeof document === 'undefined') return null;

  return Array.from(document.querySelectorAll('.p-dialog'))
    .filter(isVisible)
    .reduce((topmost, dialog) => {
      if (!topmost) return dialog;

      const zIndexDifference = dialogZIndex(dialog) - dialogZIndex(topmost);
      if (zIndexDifference > 0) return dialog;
      if (zIndexDifference < 0) return topmost;

      return topmost.compareDocumentPosition(dialog) & 4 ? dialog : topmost;
    }, null);
};

const navigationScope = (scope) => {
  if (typeof document === 'undefined') return null;

  const source = sourceElement(scope);
  const activeDialog = topmostActiveDialog();
  const sourceDialog = source?.closest?.('.p-dialog');
  // Keep the originating task. A late page error must not select errors in a
  // newly opened dialog, and a form must not select another form's old errors.
  if (source) {
    if (!isVisible(source) || (activeDialog && activeDialog !== sourceDialog)) return null;
    return source.closest('form') || source.form || source;
  }
  return activeDialog || document.getElementById('shop-management-content');
};

const bannerNavigationScope = (source) => {
  const fieldScope = navigationScope(source);
  if (!fieldScope) return null;
  const ownedRegion = source?.closest?.('[data-bayhive-error-scope]');
  if (ownedRegion) return ownedRegion;
  // A single-form overlay owns its adjacent server banner. Multi-form dialogs
  // must mark their owned error region explicitly instead of sharing errors.
  const overlay = source?.closest?.('.p-dialog, .p-popover, [data-pc-name="popover"]');
  if (overlay && overlay.querySelectorAll('form').length === 1) return overlay;
  return fieldScope;
};

const closestField = (element) =>
  element?.closest?.('.bayhive-field, [data-pc-name="formfield"], .p-formfield') || null;

const hasVisibleFieldError = (field) =>
  Array.from(field.querySelectorAll(FIELD_ERROR_SELECTOR)).some(isVisible);

const isTopLevelErrorBanner = (banner) => isElement(banner) && !closestField(banner);

const errorBanners = (scope) =>
  Array.from(scope.querySelectorAll(ERROR_BANNER_SELECTOR)).filter(isTopLevelErrorBanner);

const errorBannerSnapshot = (banner) => ({
  text: banner.textContent?.trim() || '',
  visible: isVisible(banner),
});

const errorBannerState = (scope) =>
  new Map(errorBanners(scope).map((banner) => [banner, errorBannerSnapshot(banner)]));

const errorBannerChangedSince = (banner, previousState) => {
  const previous = previousState.get(banner);
  const current = errorBannerSnapshot(banner);

  return !previous || previous.text !== current.text || previous.visible !== current.visible;
};

const firstUpdatedErrorBanner = (scope, previousState) =>
  errorBanners(scope).find(
    (banner) => isVisible(banner) && errorBannerChangedSince(banner, previousState),
  ) || null;

const compareDocumentOrder = (left, right) => {
  if (left === right) return 0;
  return left.compareDocumentPosition(right) & 4 ? -1 : 1;
};

const firstInvalidField = (scope) => {
  const fields = new Set();

  // Native form ownership also includes teleported footer controls with a
  // `form` attribute. Do not widen the search to unrelated modal forms.
  if (scope.matches('form')) {
    Array.from(scope.elements || []).forEach((control) => {
      const field = closestField(control);
      if (field && isVisible(field) && (control.matches(INVALID_CONTROL_SELECTOR) || hasVisibleFieldError(field))) fields.add(field);
    });
  }

  scope.querySelectorAll(INVALID_CONTROL_SELECTOR).forEach((control) => {
    if (!isVisible(control)) return;

    const field = closestField(control);
    if (field && isVisible(field)) fields.add(field);
  });

  scope.querySelectorAll('.bayhive-field').forEach((field) => {
    if (isVisible(field) && hasVisibleFieldError(field)) fields.add(field);
  });

  return Array.from(fields).sort(compareDocumentOrder)[0] || null;
};

const isUsableControl = (control) =>
  isVisible(control) &&
  !control.disabled &&
  control.getAttribute('aria-disabled') !== 'true' &&
  typeof control.focus === 'function' &&
  (control.matches(FOCUSABLE_CONTROL_SELECTOR) || control.tabIndex >= 0);

const focusableControl = (field) => {
  const invalidControl = Array.from(field.querySelectorAll(INVALID_CONTROL_SELECTOR)).find(
    isUsableControl,
  );

  if (invalidControl) return invalidControl;

  return Array.from(field.querySelectorAll(FOCUSABLE_CONTROL_SELECTOR)).find(isUsableControl);
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const scrollIntoView = (element) => {
  if (!isVisible(element)) return;
  const viewport = getActiveRepairOrderViewport();
  if (viewport?.navigate(element, { block: 'nearest', focus: false })) return;
  const bounds = element.getBoundingClientRect();
  let top = 0;
  let bottom = window.innerHeight;
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    if (!/(auto|scroll)/.test(window.getComputedStyle(parent).overflowY)) continue;
    const parentBounds = parent.getBoundingClientRect();
    top = Math.max(top, parentBounds.top);
    bottom = Math.min(bottom, parentBounds.bottom);
  }
  if (bounds.top >= top && bounds.bottom <= bottom) return;
  element.scrollIntoView({
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    block: 'nearest',
    inline: 'nearest',
  });
};

const focusInvalidField = (field) => {
  scrollIntoView(field);

  const control = focusableControl(field);
  if (!control) return;

  try {
    control.focus({ preventScroll: true });
  } catch {
    control.focus();
  }
};

const afterValidationRender = (callback) => {
  if (typeof window === 'undefined' || !window.requestAnimationFrame) {
    Promise.resolve().then(callback);
    return;
  }

  // PrimeVue emits submit before its resolver's state is guaranteed to be in
  // the DOM. Two frames also cover a Vue update triggered by a mapped 422.
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(callback);
  });
};

/**
 * Queue BayHive's common first-error behavior after a PrimeVue submit or a
 * mapped server validation response. `scope` accepts a native submit event,
 * an Element, or a Vue component/form ref with `$el`, `el`, or `form`.
 */
export const queueBayhiveFormErrorNavigation = (scope = null) => {
  const event = scope?.originalEvent || scope;
  const captured = event && typeof event === 'object' ? submitEventNavigations.get(event) : null;
  if (captured) {
    captured.fail();
    return;
  }
  const source = sourceElement(scope);
  if (!source && getActiveRepairOrderViewport()) return;
  const fieldScope = navigationScope(source);
  if (!fieldScope) return;
  const pending = pendingSubmitNavigations.get(fieldScope);
  if (pending) {
    pending.fail();
    return;
  }
  const owner = managedDialogNavigations.get(fieldScope.closest('.p-dialog'));
  if (owner?.isCurrent()) {
    owner.fail(fieldScope);
    return;
  }
  // Capture the scope now, not when validation finishes rendering. Multiple
  // callers in the same render share one navigation.
  const token = {};
  queuedNavigations.set(fieldScope, token);
  const events = ['pointerdown', 'keydown', 'input', 'wheel', 'touchmove'];
  const removeListeners = () => events.forEach((type) => document.removeEventListener(type, cancel, true));
  const cancel = () => {
    if (queuedNavigations.get(fieldScope) === token) queuedNavigations.delete(fieldScope);
    removeListeners();
  };
  events.forEach((type) => document.addEventListener(type, cancel, { capture: true, passive: true }));
  afterValidationRender(() => {
    removeListeners();
    if (queuedNavigations.get(fieldScope) !== token || navigationScope(source) !== fieldScope) return;
    queuedNavigations.delete(fieldScope);
    const field = firstInvalidField(fieldScope);
    if (field) focusInvalidField(field);
  });
};

const bannerScopeIsActive = (scope) => {
  if (!isVisible(scope)) return false;

  const activeDialog = topmostActiveDialog();
  return !activeDialog || activeDialog === scope || activeDialog.contains(scope);
};

const firstSubmitInvalidField = (fieldScope, bannerScope) => {
  // A disappearing form belongs to a completed/closed task, not a reason to
  // search sibling forms for another invalid control.
  return isVisible(fieldScope) && isVisible(bannerScope) ? firstInvalidField(fieldScope) : null;
};

const observeSubmitErrorBanner = (
  fieldScope,
  bannerScope,
  previousBannerState,
  { isCurrent, mayNavigate, finishAfterCheck, invalidScope, onComplete },
) => {
  if (typeof MutationObserver === 'undefined') return null;

  let observer = null;
  let timeoutId = null;
  let scheduled = false;
  let complete = false;

  const cleanup = () => {
    if (complete) return;

    complete = true;
    observer?.disconnect();
    if (timeoutId !== null) window.clearTimeout(timeoutId);
    onComplete();
  };

  const checkForError = () => {
    scheduled = false;
    if (complete) return;

    // If another dialog becomes active, this submit is no longer the active
    // task. Do not pull focus or scroll behind the new dialog.
    if (!isCurrent() || !bannerScopeIsActive(bannerScope)) {
      cleanup();
      return;
    }
    if (!mayNavigate()) return;

    const field = firstSubmitInvalidField(invalidScope(), bannerScope);
    if (field) {
      focusInvalidField(field);
      cleanup();
      return;
    }

    const banner = firstUpdatedErrorBanner(bannerScope, previousBannerState);
    if (!banner) {
      if (finishAfterCheck()) cleanup();
      return;
    }

    scrollIntoView(banner);
    cleanup();
  };

  const scheduleCheck = () => {
    if (scheduled || complete) return;

    scheduled = true;
    // Let Vue render any matching field errors in the same response before
    // treating a generic banner as the submit result.
    afterValidationRender(checkForError);
  };

  observer = new MutationObserver(scheduleCheck);
  observer.observe(bannerScope, {
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'hidden', 'style'],
    characterData: true,
    childList: true,
    subtree: true,
  });

  timeoutId = window.setTimeout(cleanup, ERROR_BANNER_OBSERVER_TIMEOUT);
  // A fast response may have already updated the DOM before the observer was
  // attached. Compare it with the state captured at submit time once.
  scheduleCheck();

  return {
    cleanup,
    scheduleCheck,
    keepAlive() {
      if (timeoutId !== null) window.clearTimeout(timeoutId);
      timeoutId = null;
    },
  };
};

const createSubmitNavigation = (source, managed = false) => {
  const fieldScope = navigationScope(source);
  const bannerScope = bannerNavigationScope(source);
  if (!source || !fieldScope || !bannerScope || !isVisible(bannerScope)) return null;
  pendingSubmitNavigations.get(fieldScope)?.complete();

  let cancelled = false;
  let observer = null;
  let failed = false;
  let handled = false;
  let explicit = managed;
  let errorScope = fieldScope;
  const dialog = fieldScope.closest('.p-dialog');
  const cancel = () => {
    cancelled = true;
    observer?.cleanup();
    document.removeEventListener('pointerdown', cancel, true);
    document.removeEventListener('keydown', cancel, true);
    document.removeEventListener('input', cancel, true);
    document.removeEventListener('wheel', cancel, true);
    document.removeEventListener('touchmove', cancel, true);
    if (pendingSubmitNavigations.get(fieldScope) === attempt) pendingSubmitNavigations.delete(fieldScope);
    if (dialog && managedDialogNavigations.get(dialog) === attempt) managedDialogNavigations.delete(dialog);
  };
  const isCurrent = () => !cancelled && !handled && navigationScope(source) === fieldScope;
  const attempt = {
    isCurrent,
    claim() {
      explicit = true;
      observer?.keepAlive();
      if (dialog && isCurrent()) managedDialogNavigations.set(dialog, attempt);
      return attempt;
    },
    complete: cancel,
    fail(scope = null) {
      if (!isCurrent()) { cancel(); return; }
      // A child validator may identify a more specific form before the parent
      // catches the failure. Keep that destination when the parent also fails.
      if (scope) errorScope = scope;
      failed = true;
      observer?.scheduleCheck();
    },
  };
  pendingSubmitNavigations.set(fieldScope, attempt);
  document.addEventListener('pointerdown', cancel, true);
  document.addEventListener('keydown', cancel, true);
  document.addEventListener('input', cancel, true);
  document.addEventListener('wheel', cancel, { capture: true, passive: true });
  document.addEventListener('touchmove', cancel, { capture: true, passive: true });
  const previousBannerState = errorBannerState(bannerScope);
  observer = observeSubmitErrorBanner(fieldScope, bannerScope, previousBannerState, {
    isCurrent,
    mayNavigate: () => !explicit || failed,
    invalidScope: () => errorScope,
    finishAfterCheck: () => explicit && failed,
    onComplete: () => { handled = true; cancel(); },
  });
  if (managed) attempt.claim();
  return attempt;
};

/** Own one async submit from validation through its server result. */
export const beginBayhiveFormSubmission = (scope) => {
  const event = scope?.originalEvent || scope;
  const captured = event && typeof event === 'object' ? submitEventNavigations.get(event) : null;
  if (captured) return captured.claim();
  const source = sourceElement(scope);
  // Programmatic autosaves are separate actions even when they share a form.
  // Only the same captured native submit may reuse its existing lifecycle.
  return createSubmitNavigation(source, true)
    || { isCurrent: () => false, complete() {}, fail() {} };
};

/**
 * Returns PrimeVue Form root pass-through options for the BayHive app setup.
 * Form submit capture lets every real Form share the same first-error path.
 */
export const installBayhiveFormErrorNavigation = () => ({
  'data-bayhive-form': 'true',
  onSubmitCapture: (event) => {
    const source = sourceElement(event);
    const attempt = createSubmitNavigation(source, source?.dataset?.bayhiveSubmitNavigation === 'managed');
    if (attempt) submitEventNavigations.set(event, attempt);
  },
});
